import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSuspenseQuery } from '@tanstack/react-query';
import { sectionsQueries } from './sectionsQueries.ts';
import { useFolderNavigation } from '../common/useFolderNavigation.ts';
import { searchFoldersWithNavigation } from '../common/searchFoldersWithNavigation.ts';
import { ROOT_ID } from '../common/folderNavigation.tsx';
import { SaveAssetDialogTitle } from '../save-asset-dialog/SaveAssetDialogTitle.tsx';
import { SectionsImportLayout } from './SectionsImportLayout.tsx';
import { SearchAssetsInput } from '../common/SearchAssetsInput.tsx';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs.tsx';
import { ImportSectionsDialogEmptyState } from './ImportSectionsDialogEmptyState.tsx';
import { ImportSectionsDialogEmptySearchResults } from './ImportSectionsDialogEmptySearchResults.tsx';
import List from '@mui/material/List';
import Button from '@mui/material/Button';
import { useImportSection } from '../../editor/hooks/useImportSection.ts';

export interface ImportSectionsDialogProps {
  onClose: () => void;
  onImported?: (addedSectionIds: string[]) => void;
  afterSectionId: string;
}

export function ImportSectionsDialogContent({ onClose, onImported, afterSectionId }: ImportSectionsDialogProps) {
  const [selectedSectionsIds, setSelectedSectionsIds] = useState<string[]>([]);
  const { t } = useTranslation();
  const importSectionWithWebSocket = useImportSection();
  const [searchTerm, setSearchTerm] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  const { data: rootFolder } = useSuspenseQuery(sectionsQueries.listFolders);
  const { breadcrumbs, currentFolder, selectFolder } = useFolderNavigation(rootFolder, 'All');

  const { folders, contents } = useMemo(() => {
    return searchFoldersWithNavigation(rootFolder, currentFolder, searchTerm);
  }, [rootFolder, currentFolder, searchTerm]);

  const handleSectionToggle = (id: string) => {
    setSelectedSectionsIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const handleImport = async () => {
    try {
      setIsImporting(true);
      const addedSectionIds = await importSectionWithWebSocket({ sourceContentIds: selectedSectionsIds, afterSectionId });
      if (addedSectionIds?.length) {
        onImported?.(addedSectionIds);
      }
    } finally {
      setIsImporting(false);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedSectionsIds([]);
    setSearchTerm('');
    onClose();
  };

  const handleFolderSelect = (folderId: string | null) => {
    selectFolder(folderId ?? ROOT_ID);
    setSearchTerm('');
  };

  const isLibraryEmpty = rootFolder.contents.length === 0 && rootFolder.subfolders.length === 0;
  const isSearchEmpty = !isLibraryEmpty && folders.length === 0 && contents.length === 0;

  return (
    <>
      <SaveAssetDialogTitle title={t('library.import_sections_dialog_title')} onClose={handleClose} />

      <SectionsImportLayout.DialogContent>
        <SearchAssetsInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

        {breadcrumbs.length > 1 && !searchTerm && <Breadcrumbs items={breadcrumbs} onSelectPath={selectFolder} sx={{ pl: 1 }} />}

        {isLibraryEmpty ? (
          <ImportSectionsDialogEmptyState />
        ) : isSearchEmpty ? (
          <ImportSectionsDialogEmptySearchResults />
        ) : (
          <List sx={{ p: 0, overflow: 'auto' }}>
            {folders.map((folder) => (
              <SectionsImportLayout.FolderListItem key={folder.id} onClick={() => handleFolderSelect(folder.id)}>
                {folder.name}
              </SectionsImportLayout.FolderListItem>
            ))}

            {contents.map((content) => (
              <SectionsImportLayout.SectionListItem
                key={content.id}
                content={{
                  id: content.id,
                  name: content.name,
                  updatedAt: content.updatedAt,
                }}
                selectedIndex={selectedSectionsIds.indexOf(content.id)}
                onToggle={handleSectionToggle}
              />
            ))}
          </List>
        )}
      </SectionsImportLayout.DialogContent>

      <SectionsImportLayout.DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('button.cancel')}
        </Button>
        <Button onClick={handleImport} variant="contained" color="primary" disabled={selectedSectionsIds.length === 0 || isImporting}>
          {selectedSectionsIds.length < 2
            ? t('library.import_sections_dialog_import_button', { count: selectedSectionsIds.length })
            : t('library.import_sections_dialog_import_button_plural', { count: selectedSectionsIds.length })}
        </Button>
      </SectionsImportLayout.DialogActions>
    </>
  );
}
